import React from "react";
import PropTypes from "prop-types";
import { PlainText } from "@learningblocks/storybook-components";
import { RectangleCard } from "@learningblocks/storybook-components";
import { NotebookImage } from "@learningblocks/shared-components";
import { HiddenComponent } from "@learningblocks/storybook-components";
import { StepSelectRedux } from "@learningblocks/storybook-components";
import { MultiSelectRedux } from "@learningblocks/storybook-components";
import { SingleSelectRedux } from "@learningblocks/storybook-components";
import { SingleSelectReduxNew } from "@learningblocks/storybook-components";
import { SliderSelectRedux } from "@learningblocks/storybook-components";
import { Utilities } from "@learningblocks/exp-constants";
const { COMPONENT_TYPE } = Utilities;
import { SliderSelectGroupRedux } from "@learningblocks/storybook-components";
import { SingleSelectColorRedux } from "@learningblocks/storybook-components";
import { SliderImage } from "@learningblocks/storybook-components";
import { HighlightProvider } from "@learningblocks/shared-components";
import { SliderWidgetContainer } from "@learningblocks/storybook-components";
import { SummaryText } from "@learningblocks/protocol-components/lib/SummaryText";
import { TakeawayMessages } from "@learningblocks/protocol-components/lib/TakeawayMessages";
import { BlottingPaper } from "@learningblocks/protocol-components/lib/BlottingPaper";
import { ImageTable } from "@learningblocks/protocol-components/lib/ImageTable";
import { TextArea } from "@learningblocks/protocol-components/lib/TextArea";
import { MaterialSection } from "@learningblocks/protocol-components/lib/MaterialSection";
import { ContextTable } from "@learningblocks/protocol-components/lib/ContextTable";
import { GelComponent } from "@learningblocks/protocol-components/lib/GelComponent";
import { RestrictionDigestPredictions } from "@learningblocks/protocol-components/lib/RestrictionDigestPredictions";
import { RestrictionDigestResults } from "@learningblocks/protocol-components/lib/RestrictionDigestResults";
import { TransformingBacteriaPredictions } from "@learningblocks/protocol-components/lib/TransformingBacteriaPredictions";
import { TransformingBacteriaResults } from "@learningblocks/protocol-components/lib/TransformingBacteriaResults";
import { Ideal57 } from "@learningblocks/protocol-components/lib/TransformingBacteriaIdeal";
import Predictions37 from "@learningblocks/protocol-components/lib/Predictions37";
import PredictionResults37 from "@learningblocks/protocol-components/lib/PredictionResults37";

/**
 * ContentComponents:
 *  - Display the contents of all Sections Except for DesignSection
 *    in Web View
 */
export function ContentComponents({ components, className }) {
  const { onPlainTextClick } = React.useContext(HighlightProvider.Context);
  const plainText = (component) => {
    const onClickHighlight = (contentId, event) => {
      onPlainTextClick(contentId, event);
    };
    return (
      <PlainText
        key={component.id}
        text={component.text}
        className={className}
        onClickHighlight={onClickHighlight}
      />
    );
  };

  const fullWidthImage = (component) => (
    <NotebookImage {...component} key={component.id} className={className} />
  );

  const sliderImage = (component) => {
    const [value, onChange] = React.useState(0);
    return (
      <SliderImage
        {...component}
        key={component.id}
        className={className}
        value={value}
        onChange={onChange}
      />
    );
  };

  const sliderWidget = (component) => {
    return (
      <SliderWidgetContainer
        {...component}
        key={component.id}
        className={className}
      />
    );
  };

  const singleSelect = (component) => (
    <SingleSelectRedux
      {...component}
      key={component.id}
      className={className}
    />
  );

  const singleSelectNew = (component) => (
    <SingleSelectReduxNew
      {...component}
      key={component.id}
      className={className}
    />
  );

  const multiSelect = (component) => (
    <MultiSelectRedux {...component} key={component.id} className={className} />
  );

  const stepSelect = (component) => {
    const options = component.options.map((option) => {
      return { value: option.id, label: option.text };
    });

    return (
      <StepSelectRedux
        {...component}
        key={component.id}
        className={className}
        options={options}
      />
    );
  };

  const sliderSelect = (component) => (
    <SliderSelectRedux
      {...component}
      key={component.id}
      className={className}
    />
  );
  const sliderSelectGroup = (component) => (
    <SliderSelectGroupRedux
      {...component}
      key={component.id}
      className={className}
    />
  );

  const singleSelectColorRedux = (component) => (
    <SingleSelectColorRedux
      {...component}
      key={component.id}
      className={className}
    />
  );

  const materialSection = (component) => {
    return (
      <MaterialSection
        key={component.id}
        materials={component.materials}
        grouping={component.groups}
      />
    );
  };

  const techniqueCard = (component) => {
    const MOBILE_WIDTH = 767;
    const currentScreenWidth = document.body.clientWidth;
    const trim = currentScreenWidth > MOBILE_WIDTH ? false : true;
    return (
      <RectangleCard
        {...component}
        key={component.id}
        className={className}
        trim={trim}
      />
    );
  };

  const summaryText = (component) => (
    <SummaryText key={component.id} {...component} />
  );

  const takeawayMessages = (component) => (
    <TakeawayMessages key={component.id} {...component} />
  );

  const blottingPaper = (component) => (
    <BlottingPaper key={component.id} {...component} />
  );

  const imageTable = (component) => (
    <ImageTable key={component.id} {...component} />
  );

  const textArea = (component) => (
    <TextArea key={component.id.toString()} {...component} />
  );

  const contextTable = (component) => (
    <ContextTable key={component.id} {...component} />
  );

  const gelComponent = (component) => (
    <GelComponent key={component.id} {...component} />
  );

  const restrictionDigestPredictions = (component) => (
    <RestrictionDigestPredictions key={component.id} {...component} />
  );

  const restrictionDigestResults = (component) => (
    <RestrictionDigestResults key={component.id} {...component} />
  );

  const transformBacteriaPredictions = (component) => (
    <TransformingBacteriaPredictions key={component.id} {...component} />
  );

  const transformBacteriaResults = (component) => (
    <TransformingBacteriaResults key={component.id} {...component} />
  );

  const ideal57 = (component) => <Ideal57 key={component.id} {...component} />;

  const ligatingDNAPredictions = (component) => (
    <Predictions37 key={component.id} {...component} />
  );

  const ligatingDNAResults = (component) => (
    <PredictionResults37 key={component.id} {...component} />
  );

  const renderComponent = (component) => {
    switch (component.type) {
      case COMPONENT_TYPE.PLAIN_TEXT:
        return plainText(component);
      case COMPONENT_TYPE.TEXT_AREA:
        return textArea(component);
      case COMPONENT_TYPE.TECHNIQUE_CARD:
        return techniqueCard(component);
      case COMPONENT_TYPE.NOTEBOOK_IMAGE:
        return fullWidthImage(component);
      case COMPONENT_TYPE.SLIDER_IMAGE:
        return sliderImage(component);
      case COMPONENT_TYPE.SLIDER_WIDGET:
        return sliderWidget(component);
      case COMPONENT_TYPE.SUMMARY_TEXT:
        return summaryText(component);
      case COMPONENT_TYPE.TAKEAWAY_MESSAGES:
        return takeawayMessages(component);
      case COMPONENT_TYPE.MATERIAL_SECTION:
        return materialSection(component);
      case COMPONENT_TYPE.BLOTTING_PAPER:
        return blottingPaper(component);
      case COMPONENT_TYPE.IMAGE_TABLE:
        return imageTable(component);
      case COMPONENT_TYPE.CONTEXT_TABLE:
        return contextTable(component);
      case COMPONENT_TYPE.GEL_COMPONENT:
        return gelComponent(component);
      case COMPONENT_TYPE.RESTRICTION_DIGEST_PREDICTIONS:
        return restrictionDigestPredictions(component);
      case COMPONENT_TYPE.RESTRICTION_DIGEST_RESULTS:
        return restrictionDigestResults(component);
      case COMPONENT_TYPE.TRANSFORMING_BACTERIA_PREDICTIONS:
        return transformBacteriaPredictions(component);
      case COMPONENT_TYPE.TRANSFORMING_BACTERIA_RESULTS:
        return transformBacteriaResults(component);
      case COMPONENT_TYPE.TRANSFORMING_BACTERIA_IDEAL:
        return ideal57(component);
      case COMPONENT_TYPE.LIGATING_DNA_PREDICTIONS:
        return ligatingDNAPredictions(component);
      case COMPONENT_TYPE.LIGATING_DNA_RESULTS:
        return ligatingDNAResults(component);
      case COMPONENT_TYPE.CSCQ:
        return singleSelect(component);
      case COMPONENT_TYPE.CSCQ_NEW:
        return singleSelectNew(component);
      case COMPONENT_TYPE.MSCQ:
        return multiSelect(component);
      case COMPONENT_TYPE.DDQ:
        return stepSelect(component);
      case COMPONENT_TYPE.GROUPED_DNDQ:
        return (
          <HiddenComponent
            visibility={component.visibility}
            key={component.id}
            className={className}
          >
            {sliderSelectGroup(component)}
          </HiddenComponent>
        );
      case COMPONENT_TYPE.DNDQ_COLOR:
        return (
          <HiddenComponent
            visibility={component.visibility}
            key={component.id}
            className={className}
          >
            {sliderSelect(component)}
          </HiddenComponent>
        );
      case COMPONENT_TYPE.CSCQ_COLOR:
        return (
          <HiddenComponent
            visibility={component.visibility}
            key={component.id}
            className={className}
          >
            {singleSelectColorRedux(component)}
          </HiddenComponent>
        );
      default:
        return <h3> Component Type {component.type}</h3>;
    }
  };

  let componentList = [];

  for (let index in components) {
    let component = components[index];
    componentList.push(renderComponent(component));
    if (component && component.finalPoint) {
      break;
    }
  }

  return componentList;
}

ContentComponents.propTypes = {
  /**
   * Array of data corresponding to component to be
   * displayed in content
   */
  components: PropTypes.arrayOf(PropTypes.object),
  /**
   * ClassName to set on each component in List
   */
  className: PropTypes.string,
};

ContentComponents.defaultProps = {};
